import AtomModel from "./AtomModel";
import BondModel from "./BondModel";
import EncoderUtil from "./EncoderUtil";
import IAtomModel from "./IAtomModel";
import IBondModel from "./IBondModel";

export default class AtomContainer {
    atoms: IAtomModel[];
    bonds: IBondModel[];

    constructor(atoms?: IAtomModel[], bonds?: IBondModel[]) {
        this.atoms = atoms? atoms : [];
        this.bonds = bonds? bonds : [];
    }

    addAtom(symbol: string): IAtomModel {
        const atom = new AtomModel(symbol);
        this.atoms.push(atom);
        return atom;
    }

    addBond(a1: IAtomModel, a2: IAtomModel, order?: number, type?: string): IBondModel {
        const bond = new BondModel(a1, a2, order || 1, type);
        this.bonds.push(bond);
        return bond;
    }

    getBondsAttachedTo(atom: IAtomModel): IBondModel[] {
        const bonds = this.bonds.filter(b => b.isAttachedTo(atom));
        return bonds || [];
    }

    countTotalElectronsForAtom(atom: IAtomModel): number {
        const attachedBonds = this.getBondsAttachedTo(atom);
        const totalOrder = attachedBonds.reduce((acc, b) => acc + b.order, 0);
        const totalElectrons = ((totalOrder + atom.lonePairs) * 2) + atom.radicals;
        return totalElectrons;
    }

    calcFormalChargeForAtom(atom: IAtomModel): number {
        if ( atom === undefined) return -100;

        // R groups (atomicNumber 0) don't have meaningful valence
        // Preserve their existing charge instead of recalculating
        if (atom.atomicNumber === 0) {
            return atom.charge;
        }

        const attachedBonds = this.getBondsAttachedTo(atom);
        const totalOrder = attachedBonds.reduce((acc, b) => acc + b.order, 0);
        const totalCharge = totalOrder + (atom.lonePairs * 2) + atom.radicals;
        return atom.valence - totalCharge;
    }

    flipChirality() {
        this.bonds.forEach(bond => bond.flipChirality());
    }

    getMoleculeOfAtom(atom: IAtomModel): AtomContainer {
        const atoms: IAtomModel[] = [];
        const bonds: IBondModel[] = [];
        const newContainer = new AtomContainer();

        this.addConnectedAtomAndBonds(atom, atoms, bonds);

        newContainer.atoms = atoms;
        newContainer.bonds = bonds;

        return newContainer;
    }

    getMolecules(): AtomContainer[] {
        let unusedAtoms = this.atoms.slice();

        const containers: AtomContainer[] = [];
        while (unusedAtoms.length > 0) {
            const atom = unusedAtoms.shift()!;
            const newContainer = this.getMoleculeOfAtom(atom);
            containers.push(newContainer);

            unusedAtoms = unusedAtoms.filter((atom) => !newContainer.atoms.includes(atom));
        }

        return containers;
    }

    addConnectedAtomAndBonds(atom: IAtomModel, atoms: IAtomModel[], bonds: IBondModel[]) {
        if (!this.atoms.includes(atom as AtomModel)) {
            return;
        }

        atoms.push(atom);

        const attachedBonds = this.getBondsAttachedTo(atom);
        const newBonds = attachedBonds.filter(b => !bonds.includes(b));
        bonds.push(...newBonds);

        newBonds.forEach(bond => {
          const otherAtom = bond.getOtherAtom(atom);
          if (otherAtom && !atoms.includes(otherAtom)) {
            this.addConnectedAtomAndBonds(otherAtom, atoms, bonds);
          }
        })
    }

    encode(): string {
        return EncoderUtil.encodeAtomContainer(this);
    }

    static decode(containerString: string): AtomContainer {
        return EncoderUtil.decodeAtomContainer(containerString);
    }

    clear(): void {
        this.atoms = [];
        this.bonds = [];
    }

    getAtomsConnectedTo(atom: IAtomModel, terminals?: IAtomModel[]): IAtomModel[] {
        const connectedAtoms: IAtomModel[] = [];
        const visited: IAtomModel[] = [];
        const toVisit: IAtomModel[] = [];


        if (terminals) {
            visited.push(...terminals);
        }

        toVisit.push(atom);
        while (toVisit.length > 0) {
            const currAtom = toVisit.pop()!;
            visited.push(currAtom);
            connectedAtoms.push(currAtom);

            const bonds = this.getBondsAttachedTo(currAtom);
            bonds.forEach(bond => {
                const otherAtom = bond.getOtherAtom(currAtom);
                if (!visited.includes(otherAtom) && !toVisit.includes(otherAtom)) {
                    toVisit.push(otherAtom);
                }
            })
        }
        return connectedAtoms;
    }

    combine(container: AtomContainer) {
        this.atoms.push(...container.atoms);
        this.bonds.push(...container.bonds);
    }
    split(bond: IBondModel) {
        const a1 = bond.a1;
        const a2 = bond.a2;
        this.bonds.splice(this.bonds.indexOf(bond), 1);
        const container1 = this.getMoleculeOfAtom(a1);
        const container2 = this.getMoleculeOfAtom(a2);
        this.bonds.push(bond);
        return {container1, container2};
    }

    deepCopy(){
        const atomsCopy = this.atoms.map(a => a.copy());
        const bondsCopy  = this.bonds.map(b => b.copy(atomsCopy[this.atoms.indexOf(b.a1)], atomsCopy[this.atoms.indexOf(b.a2)]));

        return new AtomContainer(atomsCopy, bondsCopy);
    }

    getBranch(bond: IBondModel, branchAtom: IAtomModel) {
        const split = this.split(bond);
        if(split.container1.atoms.includes(branchAtom)) return split.container1;
        else if(split.container2.atoms.includes(branchAtom)) return split.container2;

        return;
    }
    getAttachedAtomsAndLevels(a: IAtomModel, depth: number = 3) {
        const atomsAndLevels: IAtomModel[][] = [];
        const visitedAtoms = new Set<IAtomModel>();
        const candidates = [a];
        for (let i = 0; i < depth; i++) {
            const candidatesThisDepth: IAtomModel[] = [];
            while (candidates.length) {
                const candidate = candidates.pop()!;
                visitedAtoms.add(candidate);
                const connected = this.getBondsAttachedTo(candidate).map(bond => bond.getOtherAtom(candidate));
                const filtered = connected.filter(a => !visitedAtoms.has(a));
                candidatesThisDepth.push(...filtered);
            }

            if (candidatesThisDepth.length === 0) break;
            candidates.push(...Array.from(new Set(candidatesThisDepth)));
            atomsAndLevels[i] = [...candidates];
        }
        return atomsAndLevels;
    }

    /**
     * Look up an atom by its ID.
     * Used for ID-based arrow pushing (MC-680).
     *
     * @param id - The atom ID (e.g., 'atom-5')
     * @returns The atom with that ID, or undefined if not found
     */
    getAtomById(id: string): IAtomModel | undefined {
        return this.atoms.find(a => a.id === id);
    }

    /**
     * Look up a bond by its ID.
     * Used for ID-based arrow pushing (MC-680).
     *
     * @param id - The bond ID (e.g., 'bond-3')
     * @returns The bond with that ID, or undefined if not found
     */
    getBondById(id: string): IBondModel | undefined {
        return this.bonds.find(b => b.id === id);
    }
}
